import { useState } from 'react';
import { InventoryItem } from '../page';

interface InventoryDetailProps {
  items: InventoryItem[];
  onUpdateStock: (variantId: string, newQuantity: number) => void;
  onBack: () => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  filterCategory: string;
  onFilterCategoryChange: (category: string) => void;
  filterStatus: string;
  onFilterStatusChange: (status: string) => void;
  categories: string[];
}

export default function InventoryDetail({
  items,
  onUpdateStock,
  onBack,
  searchTerm,
  onSearchChange,
  filterCategory,
  onFilterCategoryChange,
  filterStatus,
  onFilterStatusChange,
  categories
}: InventoryDetailProps) {
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [newQuantity, setNewQuantity] = useState(0);
  const [sortBy, setSortBy] = useState<'name' | 'quantity' | 'value' | 'status'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(price);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_stock':
        return 'bg-green-100 text-green-800';
      case 'low_stock':
        return 'bg-orange-100 text-orange-800';
      case 'out_of_stock':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'in_stock':
        return 'Còn hàng';
      case 'low_stock':
        return 'Sắp hết';
      case 'out_of_stock':
        return 'Hết hàng';
      default:
        return 'Không xác định';
    }
  };

  const handleSort = (field: 'name' | 'quantity' | 'value' | 'status') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortedItems = [...items].sort((a, b) => {
    let aValue: any, bValue: any;
    
    switch (sortBy) {
      case 'name':
        aValue = a.productName.toLowerCase();
        bValue = b.productName.toLowerCase();
        break;
      case 'quantity':
        aValue = a.quantity;
        bValue = b.quantity;
        break;
      case 'value':
        aValue = a.totalValue;
        bValue = b.totalValue;
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        return 0;
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const startEditing = (itemId: string, currentQuantity: number) => {
    setEditingItem(itemId);
    setNewQuantity(currentQuantity);
  };

  const handleUpdateStock = (itemId: string) => {
    if (newQuantity >= 0) {
      onUpdateStock(itemId, newQuantity);
      setEditingItem(null);
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return 'ri-arrow-up-down-line text-gray-400';
    return sortOrder === 'asc' ? 'ri-arrow-up-line text-orange-600' : 'ri-arrow-down-line text-orange-600';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <button
            onClick={onBack}
            className="text-gray-600 hover:text-gray-800 p-1"
          >
            <i className="ri-arrow-left-line text-xl sm:text-2xl"></i>
          </button>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Chi tiết tồn kho</h2>
            <p className="text-sm text-gray-600 hidden sm:block">Quản lý số lượng từng sản phẩm theo size và màu</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4">
        <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-4">
          {/* Search */}
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder="Tìm kiếm sản phẩm..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Category Filter */}
          <select
            value={filterCategory}
            onChange={(e) => onFilterCategoryChange(e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-8"
          >
            <option value="">Tất cả danh mục</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => onFilterStatusChange(e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 pr-8"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="in_stock">Còn hàng</option>
            <option value="low_stock">Sắp hết</option>
            <option value="out_of_stock">Hết hàng</option>
          </select>

          {/* Results Count */}
          <div className="flex items-center text-xs sm:text-sm text-gray-600 bg-gray-50 px-3 py-2.5 rounded-lg">
            <i className="ri-file-list-line mr-2"></i>
            <span className="font-medium">{sortedItems.length}</span>
            <span className="hidden sm:inline ml-1">sản phẩm</span>
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="block sm:hidden space-y-3">
        {sortedItems.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start space-x-3">
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.productName}
                  className="w-16 h-16 object-cover object-center rounded-lg flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-image-line text-gray-400 text-xl"></i>
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{item.productName}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{item.category}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.size} - {item.color}</p>
                  </div>
                  
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(item.status)}`}>
                    {getStatusText(item.status)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center space-x-4">
                    {editingItem === item.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={newQuantity}
                          onChange={(e) => setNewQuantity(parseInt(e.target.value) || 0)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                          min="0"
                        />
                        <button
                          onClick={() => handleUpdateStock(item.id)}
                          className="text-green-600 hover:text-green-700 p-1"
                        >
                          <i className="ri-check-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => setEditingItem(null)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <i className="ri-close-line text-lg"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className="text-lg font-bold text-gray-900">{item.quantity}</span>
                        <span className="text-xs text-gray-500">còn lại</span>
                        <button
                          onClick={() => startEditing(item.id, item.quantity)}
                          className="text-orange-600 hover:text-orange-700 p-1"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{formatPrice(item.totalValue)}</p>
                    <p className="text-xs text-gray-500">{formatPrice(item.unitPrice)}/sp</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table View */}
      <div className="hidden sm:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sản phẩm
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Tên sản phẩm</span>
                    <i className={getSortIcon('name')}></i>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size/Màu
                </th>
                <th 
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('quantity')}
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Số lượng</span>
                    <i className={getSortIcon('quantity')}></i>
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center justify-center space-x-1">
                    <span>Trạng thái</span>
                    <i className={getSortIcon('status')}></i>
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Đơn giá
                </th>
                <th 
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('value')}
                >
                  <div className="flex items-center justify-end space-x-1">
                    <span>Tổng giá trị</span>
                    <i className={getSortIcon('value')}></i>
                  </div>
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.productName}
                        className="w-12 h-12 object-cover object-center rounded-lg"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                        <i className="ri-image-line text-gray-400"></i>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{item.productName}</div>
                      <div className="text-sm text-gray-500">{item.category}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{item.size}</div>
                    <div className="text-sm text-gray-500">{item.color}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {editingItem === item.id ? (
                      <div className="flex items-center justify-center space-x-2">
                        <input
                          type="number"
                          value={newQuantity}
                          onChange={(e) => setNewQuantity(parseInt(e.target.value) || 0)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                          min="0"
                        />
                        <button
                          onClick={() => handleUpdateStock(item.id)}
                          className="text-green-600 hover:text-green-700 p-1"
                        >
                          <i className="ri-check-line"></i>
                        </button>
                        <button
                          onClick={() => setEditingItem(null)}
                          className="text-red-600 hover:text-red-700 p-1"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <span className="text-lg font-bold text-gray-900">{item.quantity}</span>
                        <button
                          onClick={() => startEditing(item.id, item.quantity)}
                          className="text-orange-600 hover:text-orange-700 p-1"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(item.status)}`}>
                      {getStatusText(item.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                    {formatPrice(item.unitPrice)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                    {formatPrice(item.totalValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => startEditing(item.id, item.quantity)}
                      className="text-orange-600 hover:text-orange-700 font-medium text-sm"
                    >
                      Cập nhật
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {sortedItems.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy sản phẩm</h3>
            <p className="text-gray-600">Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm</p>
          </div>
        )}
      </div>

      {/* Mobile Empty State */}
      {sortedItems.length === 0 && (
        <div className="block sm:hidden text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy sản phẩm</h3>
          <p className="text-gray-600 text-sm px-4">Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm</p>
        </div>
      )}

      {/* Summary */}
      {sortedItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Tổng kết</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="text-center bg-gray-50 rounded-lg p-3">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {sortedItems.length}
              </p>
              <p className="text-xs sm:text-sm text-gray-600">Biến thể</p>
            </div>
            <div className="text-center bg-gray-50 rounded-lg p-3">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {sortedItems.reduce((sum, item) => sum + item.quantity, 0)}
              </p>
              <p className="text-xs sm:text-sm text-gray-600">Tổng số lượng</p>
            </div>
            <div className="text-center bg-gray-50 rounded-lg p-3 col-span-2 sm:col-span-1">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {formatPrice(sortedItems.reduce((sum, item) => sum + item.totalValue, 0))}
              </p>
              <p className="text-xs sm:text-sm text-gray-600">Tổng giá trị</p>
            </div>
            <div className="text-center bg-red-50 rounded-lg p-3 col-span-2 sm:col-span-1">
              <p className="text-xl sm:text-2xl font-bold text-red-600">
                {sortedItems.filter(item => item.status === 'low_stock' || item.status === 'out_of_stock').length}
              </p>
              <p className="text-xs sm:text-sm text-gray-600">Cần nhập thêm</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
